import { isPlainObject } from './redact-pii';

export type SerializedErrorForLog = {
  /**
   * The error's class (`Error.name`). Not called `name`: that is a PII key, so `redactPii` would
   * blank it wherever a serialized error is redacted again (e.g. by `/api/client-log`).
   */
  errorName?: string;
  /** The error message, or {@link DATABASE_ERROR_MESSAGE_PLACEHOLDER} for a database error. */
  message: string;
  /** A driver/runtime error code (e.g. a Postgres SQLSTATE or a Node `ERR_*`), when present. */
  code?: string;
  stack?: string;
  cause?: SerializedErrorForLog;
};

export type SerializeErrorForLogOptions = {
  /** Omit `stack` (e.g. for logs relayed from the browser, whose body size is capped). */
  includeStack?: boolean;
  /** Cap each `message` at this many characters. Defaults to 2,000. */
  maxMessageLength?: number;
  /** Follow `cause` at most this many levels. Defaults to 5. */
  maxCauseDepth?: number;
};

/** Stands in for the message of an error that carries a SQLSTATE `code`. */
export const DATABASE_ERROR_MESSAGE_PLACEHOLDER = '[omitted: database error]';

/** Causes nested deeper than this are dropped, which also stops a cyclic `cause` chain. */
const MAX_CAUSE_DEPTH = 5;
const MAX_MESSAGE_LENGTH = 2_000;
const MAX_STACK_LENGTH = 4_000;

/**
 * A SQLSTATE: five digits or uppercase letters. Every SQLSTATE Postgres defines contains a digit,
 * which keeps Node's five-letter errno codes (`EPIPE`, `EPERM`, …) from matching.
 */
const SQLSTATE_PATTERN = /^(?=.*\d)[0-9A-Z]{5}$/;

const truncate = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max)}…[truncated]` : value;

/**
 * Flatten an unknown thrown value into a JSON-safe object with stack on one field.
 *
 * Only `errorName`, `message`, `code`, `stack` and `cause` are kept, so driver fields such as
 * `pg`'s `detail` (which quotes row values) never reach the logs.
 *
 * What is and isn't protected:
 * - An error whose `code` is a SQLSTATE (a database error) keeps its `code`, but its `message` is
 *   replaced with {@link DATABASE_ERROR_MESSAGE_PLACEHOLDER} and its stack keeps only the `at`
 *   frames, because Postgres messages can quote input values
 *   (`invalid input syntax for type uuid: "…"`). This is decided for each level of `cause`.
 * - Every other error's `message` (and stack header) is logged verbatim, including a wrapper
 *   whose message copies a database error's. Nothing scans message text: `redactPii` redacts by
 *   key only.
 */
export function serializeErrorForLog(
  error: unknown,
  options: SerializeErrorForLogOptions = {},
): SerializedErrorForLog {
  return serialize(
    error,
    {
      includeStack: options.includeStack ?? true,
      maxMessageLength: options.maxMessageLength ?? MAX_MESSAGE_LENGTH,
      maxCauseDepth: options.maxCauseDepth ?? MAX_CAUSE_DEPTH,
    },
    0,
  );
}

function serialize(
  error: unknown,
  options: Required<SerializeErrorForLogOptions>,
  depth: number,
): SerializedErrorForLog {
  if (error instanceof Error) {
    const { code } = error as { code?: unknown };
    const isDatabaseError = typeof code === 'string' && SQLSTATE_PATTERN.test(code);
    const stack = !options.includeStack
      ? undefined
      : isDatabaseError
        ? stackFramesOnly(error)
        : error.stack;
    const cause =
      error.cause !== undefined && depth < options.maxCauseDepth
        ? serialize(error.cause, options, depth + 1)
        : undefined;

    return {
      errorName: error.name,
      message: isDatabaseError
        ? DATABASE_ERROR_MESSAGE_PLACEHOLDER
        : truncate(error.message, options.maxMessageLength),
      ...(typeof code === 'string' || typeof code === 'number' ? { code: String(code) } : {}),
      ...(stack ? { stack: truncate(stack, MAX_STACK_LENGTH) } : {}),
      ...(cause ? { cause } : {}),
    };
  }

  return { message: truncate(String(error), options.maxMessageLength) };
}

/** The stack without its header, which repeats the message: a placeholder line, then `at` frames. */
function stackFramesOnly(error: Error): string | undefined {
  if (!error.stack) return undefined;
  const frames = error.stack.split('\n').filter((line) => /^\s+at /.test(line));
  return [`${error.name}: ${DATABASE_ERROR_MESSAGE_PLACEHOLDER}`, ...frames].join('\n');
}

/**
 * Replace every `Error` in a log payload (at any depth of plain objects and arrays) with
 * {@link serializeErrorForLog}'s output. `JSON.stringify` turns an `Error` into `{}`, so without
 * this a payload like `{ error }` loses the name, message and stack. Other values pass through.
 */
export function serializeErrorsInPayload<T>(
  value: T,
  options: SerializeErrorForLogOptions = {},
): T {
  return walk(value, options, new WeakSet<object>()) as T;
}

function walk(
  value: unknown,
  options: SerializeErrorForLogOptions,
  seen: WeakSet<object>,
): unknown {
  if (value instanceof Error) {
    return serializeErrorForLog(value, options);
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    return value.map((item) => walk(item, options, seen));
  }

  if (isPlainObject(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = walk(val, options, seen);
    }
    return result;
  }

  return value;
}
